import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { resolveFileUrl } from "@/utils";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Wand2, Upload, X } from "lucide-react";

const CATEGORIES = [
  { value: "starter", label: "Entrant" },
  { value: "main", label: "Principal" },
  { value: "dessert", label: "Postres" },
  { value: "drink", label: "Beguda" },
  { value: "side", label: "Guarnició" },
  { value: "sauce", label: "Salsa" },
  { value: "bread", label: "Pa" },
  { value: "other", label: "Altres" },
];

export function RecipeBasicInfo({
  form, setForm, photoInputRef, uploadingPhoto,
  handlePhotoUpload, generatingDetails, generateDetails,
  totalIngGrams,
}) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Nom del plat *</Label>
          <Input
            placeholder="Ex: Pizza Fina Margarita"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="recipe-name-input"
          />
        </div>
        <div>
          <Label>Categoria</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="recipe-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Photo */}
      <div>
        <Label>Fotografia del plat</Label>
        <div className="mt-2 flex flex-col gap-3">
          {form.photo_url ? (
            <div className="relative">
              <img src={resolveFileUrl(form.photo_url)} alt="Recipe" className="w-full h-48 object-cover rounded-lg border border-gray-200" />
              <button type="button" onClick={() => setForm({ ...form, photo_url: "" })} className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} className="border-2 border-dashed border-gray-300 hover:border-[#7B2D3F] rounded-lg p-6 text-center transition-colors disabled:opacity-50">
              {uploadingPhoto ? (
                <><Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin text-[#7B2D3F]" /><p className="text-sm text-gray-600">Pujant fotografia...</p></>
              ) : (
                <><Upload className="w-5 h-5 mx-auto mb-2 text-[#7B2D3F]" /><p className="text-sm font-medium text-gray-700">Feu clic per pujar fotografia</p><p className="text-xs text-gray-500 mt-1">PNG, JPG fins a 5MB</p></>
              )}
            </button>
          )}
          <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
        </div>
      </div>

      {/* Codification */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-sm mb-3">Codificació</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { key: "reference", label: "Referència", ph: "Ex: 21302" },
            { key: "ean13", label: "EAN-13", ph: "8437016654521" },
            { key: "ean13c", label: "EAN-13C", ph: "8437016654538" },
            { key: "ean14", label: "EAN-14", ph: "28437016654525" },
          ].map(f => (
            <div key={f.key}>
              <Label>{f.label}</Label>
              <Input placeholder={f.ph} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
            </div>
          ))}
        </div>
      </div>

      {/* Product characteristics */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-sm mb-3">Característiques del producte</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Pes (g)</Label>
            <Input type="number" placeholder="310" value={totalIngGrams > 0 ? totalIngGrams : form.weight_unit} onChange={(e) => setForm({ ...form, weight_unit: e.target.value })} className={totalIngGrams > 0 ? "bg-emerald-50 border-emerald-300" : ""} />
            {totalIngGrams > 0 && <p className="text-[10px] text-emerald-600 mt-0.5">Auto: suma ingredients</p>}
          </div>
          <div>
            <Label>Dimensions</Label>
            <Input placeholder="Ex: 27-28cm de diàmetre" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
          </div>
        </div>
      </div>

      {/* Description + AI */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Descripció</Label>
          <Button type="button" variant="ghost" size="sm" onClick={generateDetails} disabled={generatingDetails || (!form.description && !form.name)} className="gap-1 text-[#7B2D3F]" data-testid="recipe-ai-generate-btn">
            {generatingDetails ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
            {generatingDetails ? 'Generant...' : 'Generar amb IA'}
          </Button>
        </div>
        <Textarea placeholder="Breu descripció del plat..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-16 resize-none" data-testid="recipe-description-input" />
      </div>
    </>
  );
}
